import type { ApiClientV2Default } from 'utils/api-client/api-client.utils'
import { FromSwagger, Nullable } from 'types/helpers'

import { AddressAttributes, CompanyAttributes, VerificationSessionAttributes } from 'types/__generated__/api'

export type CompanyResponse = FromSwagger<CompanyAttributes>
export type AddressResponse = FromSwagger<AddressAttributes>
export type CompanySessionResponse = FromSwagger<VerificationSessionAttributes> & {
  documentsRequested?: boolean
  draftLegalEntityData: {
    addressAttributes?: AddressResponse
    vatCategory?: Nullable<string>
  }
}
export type CompanyWithAddressResponse = CompanyResponse & {
  attributes: AddressResponse
}

const COMPANIES_URL = '/profile_journey/companies'

export interface RepresentativeReqData {
  representativeFirstName: string
  representativeLastName: string
  representativeMiddleName: string
  representativeDateOfBirth: string
  representativePhone: string
  representativeSalutation: Nullable<string>
  representativeNationality: string
}

export interface AddressReqData {
  line1: string
  line2: string
  city: string
  postal_code: string // should be snake_case
  country_code: string // should be snake_case
  state?: string
}

export interface CompanyEntityReqData {
  companyName: string
  representativeFirstName: string
  representativeLastName: string
  vatNumber: Nullable<string>
  address?: AddressReqData
}

export interface TaxReqData {
  companyName: string
  registrationNumber: string
  taxNumber: string
  vatNumber: Nullable<string>
  vatCategory: string
  vatExemptReason: string
  euWideThreshold: boolean
  businessLegalForm: string
}

// Helper to build the main data object
const buildDataObject = (type: string, attributes: object = {}, id?: string) => ({
  ...(id && { id }),
  type,
  attributes,
})

export const createCompaniesEntityApi = ({ POST, PATCH, GET }: ApiClientV2Default) => ({
  createEntity: (data?: CompanyEntityReqData) =>
    POST<CompanyResponse>(COMPANIES_URL, {
      data: buildDataObject('companies', data),
    }),
  updateEntity: (id: string, data: CompanyEntityReqData) =>
    PATCH<CompanyResponse>(`${COMPANIES_URL}/${id}`, {
      data: buildDataObject('companies', data, id),
    }),
  fetchEntity: (id: string) =>
    GET<CompanyWithAddressResponse>(`${COMPANIES_URL}/${id}`, {
      include: ['address'],
    }),
  updateRepresentative: (id: string, data: RepresentativeReqData) =>
    PATCH<CompanyResponse>(`${COMPANIES_URL}/${id}/representative`, {
      data: buildDataObject('companies', data),
    }),
  updateAddress: (id: string, data: AddressReqData) =>
    PATCH<CompanyResponse>(`${COMPANIES_URL}/${id}/address`, {
      data: buildDataObject('companies', data),
    }),
  updateTax: (id: string, data: TaxReqData) =>
    PATCH<CompanyResponse>(`${COMPANIES_URL}/${id}/tax`, {
      data: buildDataObject('companies', data),
    }),
  getCurrentSession: (id: string) =>
    GET<CompanySessionResponse>(`${COMPANIES_URL}/${id}/verification_sessions/current`),
  createSession: (id: string) => POST<CompanySessionResponse>(`${COMPANIES_URL}/${id}/verification_sessions`),
})

export type CompaniesEntityApi = ReturnType<typeof createCompaniesEntityApi>
